import React, { useContext, useLayoutEffect } from 'react';

import { dashboardPartnerApis } from '@apis/next/partnerApis';
import roundAddSvg from '@assets/img/icons/round-add.svg';
import roundSubtractSvg from '@assets/img/icons/round-subtract.svg';
import { PartnerContext, PartnerContextValuesBase } from '@components/admin/widgets/helpers';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogTitle,
} from '@components/Dialog';
import { CheckboxInput } from '@components/NextForms/CheckboxInput';
import { IndexedLabel, IndexedFormContainer } from '@components/NextForms/IndexedForm';
import { TextInput } from '@components/NextForms/TextInput';
import { QueryContainer } from '@components/QueryContainer';
import { partnerStrings } from '@data/partners';
import { useCutoffAutoFill, usePaymentAutoFill } from '@hooks/next/usePaymentConditionAutoComplete';
import { useDialogKey } from '@hooks/useDialogKey';
import { useFormUtils } from '@hooks/useFormUtils';
import {
  PostPartnerContact,
  PartnerHelper,
  PartnerType,
  initialPartner,
  PartnerForm,
} from '@models/next/Partner';
import {
  MobxZodArrayField,
  empty,
  getDecodeResult,
  getDecodeResultOr,
} from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { formStrings, joinKeyPath } from '@utils/forms';
import { createStrings } from '@utils/local';
import { useShallowMemo } from '@utils/useShallowMemo';
import { observer } from 'mobx-react';
import * as R from 'remeda';
import { ZodArray } from 'zod';

const strings = createStrings({
  ja: {
    title: () => ({
      create: `${partnerStrings.truckCompany}を作成する`,
      update: `${partnerStrings.truckCompany}を編集する`,
    }),
    importFromFC: '基幹システムから導入する',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  form: {
    ...common.flexColumn,
    flexShrink: 1,
    minHeight: 0,
  },
  dialogContent: {
    padding: '1.5rem 100px',
    overflow: 'scroll',
    maxHeight: 550,
  },
  truckCompanyDivider: {
    ...common.colSpanFull,
    borderBottom: `1px dashed ${theme.palette.grey[400]}`,
    marginLeft: 18,
    marginTop: 10,
    marginBottom: 10,
  },
  truckCompanyContactInputContainer: {
    display: 'grid',
    gridTemplateColumns: '10fr 16fr 16fr 28px 28px',
    columnGap: 4,
    rowGap: 12,
    paddingTop: '0 !important',
  },
  truckCompanyContactsInputButton: {
    ...common.flexCenter,
    ...common.selfStart,
    paddingTop: 4,
  },
  emptyButtonContainer: {
    ...common.flexInline,
    height: 32,
  },
  paymentConditionsContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
    columnGap: 25,
    rowGap: 10,
  },
  label: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const PartnerContactsInput: React.FC<{
  field: MobxZodArrayField<ZodArray<typeof PostPartnerContact>>;
  editContact?: boolean;
}> = observer(({ field, editContact }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  if (!field.decodeResult.success) {
    return null;
  }

  const { data } = field.decodeResult;

  useLayoutEffect(() => {
    if (editContact) {
      document
        .querySelector(`*[name="${joinKeyPath('contacts', data.length - 1, 'name')}"]`)
        ?.scrollIntoView();
    }
  }, [data, editContact]);

  const renderControl = (i?: number) => (
    <>
      {i !== undefined && (
        <button
          type="button"
          className={classes.truckCompanyContactsInputButton}
          onClick={() => field.splice(i, 1, [])}
        >
          <img src={roundSubtractSvg} alt="Delete Contact" />
        </button>
      )}

      <button
        type="button"
        className={classes.truckCompanyContactsInputButton}
        onClick={() => field.push(empty)}
      >
        <img src={roundAddSvg} alt="Add Contact" />
      </button>
    </>
  );

  const renderNonEmpty = () => (
    <>
      {field.elements.map((field, i) => (
        <React.Fragment key={i}>
          <div>
            <TextInput
              field={field.fields.name}
              inputProps={{ placeholder: partnerStrings.contactName }}
            />
          </div>

          <div>
            <TextInput
              field={field.fields.email}
              inputProps={{ placeholder: partnerStrings.email }}
            />
          </div>

          <div>
            <TextInput
              field={field.fields.phone}
              inputProps={{
                placeholder: `${partnerStrings.phone}（${formStrings.optional}）`,
              }}
            />
          </div>

          {renderControl(i)}
        </React.Fragment>
      ))}
    </>
  );

  const renderEmpty = () => <div className={classes.emptyButtonContainer}>{renderControl()}</div>;

  return (
    <>
      <IndexedLabel field={field} required />

      <div className={classes.truckCompanyContactInputContainer}>
        {field.elements.length > 0 ? renderNonEmpty() : renderEmpty()}
      </div>
    </>
  );
});

const PartnerDialogContent: React.FC = observer(() => {
  const { type, onClose, partnerId, initialOutput, editContact } = useContext(PartnerContext);

  const form = useForm(PartnerForm, { initialOutput });

  const { fields } = form.root;

  const partnerQuery = dashboardPartnerApis.useGet('/dashboard/partner');
  const postPartner = dashboardPartnerApis.usePost('/dashboard/partner');
  const patchPartner = dashboardPartnerApis.usePatch('/dashboard/partner/:id', {
    params: { id: partnerId! },
  });

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [postPartner],
  });

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { dateDisabled: genbaraiCutoffSelectedDayDisabled } = useCutoffAutoFill({
    dayField: fields.payment_conditions.fields.現払い.fields.cutoff_date_day,
    selectedDayField: fields.payment_conditions.fields.現払い.fields.cutoff_date_selected_day,
    monthField: fields.payment_conditions.fields.現払い.fields.cutoff_date_month,
  });

  const { dateDisabled: genbaraiPaymentSelectedDayDisabled } = usePaymentAutoFill({
    dayField: fields.payment_conditions.fields.現払い.fields.payment_date_day,
    selectedDayField: fields.payment_conditions.fields.現払い.fields.payment_date_selected_day,
    monthField: fields.payment_conditions.fields.現払い.fields.payment_date_month,
  });

  const { dateDisabled: shimebaraiCutoffSelectedDayDisabled } = useCutoffAutoFill({
    dayField: fields.payment_conditions.fields.締払い.fields.cutoff_date_day,
    selectedDayField: fields.payment_conditions.fields.締払い.fields.cutoff_date_selected_day,
    monthField: fields.payment_conditions.fields.締払い.fields.cutoff_date_month,
  });

  const { dateDisabled: shimebaraiPaymentDateSelectedDayDisabled } = usePaymentAutoFill({
    dayField: fields.payment_conditions.fields.締払い.fields.payment_date_day,
    selectedDayField: fields.payment_conditions.fields.締払い.fields.payment_date_selected_day,
    monthField: fields.payment_conditions.fields.締払い.fields.payment_date_month,
  });

  const decodeType = getDecodeResultOr(fields.type.decodeResult, [] as PartnerType[]);

  return (
    <>
      <ActionableDialogTitle textAlign="center">{strings.title()[type]}</ActionableDialogTitle>

      <form
        className={classes.form}
        {...form.bindForm({
          async onSubmit(values) {
            if (type === 'create') {
              await postPartner.mutateAsync(values);
            } else {
              await patchPartner.mutateAsync(values);
            }
            partnerQuery.invalidate();
            onClose();
          },
        })}
      >
        <div className={classes.dialogContent}>
          <IndexedFormContainer>
            <div className={classes.colSpanFull}>{renderAlertsAndSpinnerModal()}</div>

            <IndexedLabel field={fields.name_en_short} required />
            <div>
              <TextInput field={fields.name_en_short} />
            </div>

            <IndexedLabel field={fields.fc_no} required />
            <div>
              <TextInput field={fields.fc_no} />
            </div>

            <IndexedLabel field={fields.type} required />
            <div>
              {R.filter(PartnerType.options, (i) => i !== 'その他').map((item) => (
                <CheckboxInput
                  field={fields.type}
                  value={item}
                  key={item}
                  disabled={decodeType.length === 1 && decodeType.includes('その他')}
                  label={<span>{item}</span>}
                />
              ))}
              <CheckboxInput
                field={fields.type}
                value={'その他'}
                key={'その他'}
                disabled={decodeType.length > 0 && !decodeType.includes('その他')}
                label={<span>{'その他'}</span>}
              />
            </div>

            {getDecodeResult(fields.type.decodeResult)?.includes('その他') && (
              <>
                <IndexedLabel field={fields.type_description} required />
                <div>
                  <TextInput field={fields.type_description} multiline={true} />
                </div>
              </>
            )}

            <IndexedLabel field={fields.registration_number} />
            <div>
              <TextInput field={fields.registration_number} />
            </div>

            <IndexedLabel field={fields.corporate_number} />
            <div>
              <TextInput field={fields.corporate_number} />
            </div>

            <div className={classes.truckCompanyDivider} />

            {/* TODO: Call api and restore the button */}
            {/* <div className={classes.colSpanFull} style={{ paddingLeft: 16 }}>
              <Button
                color="primary"
                text={strings.importFromFC}
                size="small"
                shape="round"
                className={classes.selfStart}
              />
            </div> */}

            <IndexedLabel field={fields.name_ja} required />
            <div>
              <TextInput field={fields.name_ja} />
            </div>

            <IndexedLabel field={fields.name_en} />
            <div>
              <TextInput field={fields.name_en} />
            </div>

            <IndexedLabel field={fields.address} />
            <div>
              <TextInput field={fields.address} />
            </div>

            <IndexedLabel field={fields.tel} required />
            <div>
              <TextInput field={fields.tel} />
            </div>

            <IndexedLabel field={fields.fax} />
            <div>
              <TextInput field={fields.fax} />
            </div>

            <IndexedLabel field={fields.payment_conditions} />
            <div>
              <div className={classes.paymentConditionsContainer}>
                <div className={classes.label}>{partnerStrings.genbarai}</div>
                <div className={classes.label}>{partnerStrings.cutoffDate}</div>
                <TextInput
                  field={fields.payment_conditions.fields.現払い.fields.cutoff_date_month}
                />
                <TextInput
                  field={fields.payment_conditions.fields.現払い.fields.cutoff_date_selected_day}
                  disabled={genbaraiCutoffSelectedDayDisabled}
                  inputProps={{ placeholder: '1～31' }}
                  withErrorMessages={false}
                />
                <TextInput field={fields.payment_conditions.fields.現払い.fields.cutoff_date_day} />

                <div />
                <div className={classes.label}>{partnerStrings.paymentDate}</div>
                <TextInput
                  field={fields.payment_conditions.fields.現払い.fields.payment_date_month}
                />
                <TextInput
                  field={fields.payment_conditions.fields.現払い.fields.payment_date_selected_day}
                  disabled={genbaraiPaymentSelectedDayDisabled}
                  inputProps={{ placeholder: '1～31' }}
                  withErrorMessages={false}
                />
                <TextInput
                  field={fields.payment_conditions.fields.現払い.fields.payment_date_day}
                />

                <div className={classes.label}>{partnerStrings.shimebarai}</div>
                <div className={classes.label}>{partnerStrings.cutoffDate}</div>
                <TextInput
                  field={fields.payment_conditions.fields.締払い.fields.cutoff_date_month}
                />
                <TextInput
                  field={fields.payment_conditions.fields.締払い.fields.cutoff_date_selected_day}
                  disabled={shimebaraiCutoffSelectedDayDisabled}
                  inputProps={{ placeholder: '1～31' }}
                  withErrorMessages={false}
                />
                <TextInput field={fields.payment_conditions.fields.締払い.fields.cutoff_date_day} />

                <div />
                <div className={classes.label}>{partnerStrings.paymentDate}</div>
                <TextInput
                  field={fields.payment_conditions.fields.締払い.fields.payment_date_month}
                />
                <TextInput
                  field={fields.payment_conditions.fields.締払い.fields.payment_date_selected_day}
                  disabled={shimebaraiPaymentDateSelectedDayDisabled}
                  inputProps={{ placeholder: '1～31' }}
                  withErrorMessages={false}
                />
                <TextInput
                  field={fields.payment_conditions.fields.締払い.fields.payment_date_day}
                />
              </div>
            </div>

            <div className={classes.truckCompanyDivider} />

            <IndexedLabel field={fields.memo1} />
            <div>
              <TextInput field={fields.memo1} multiline />
            </div>
            <IndexedLabel field={fields.memo2} />
            <div>
              <TextInput field={fields.memo2} multiline />
            </div>

            <PartnerContactsInput field={fields.contacts} editContact={editContact} />
          </IndexedFormContainer>
        </div>

        <ActionableDialogActions justifyContent="center">
          <Button
            color="primary"
            text={formStrings.ok}
            shape="round"
            size="small"
            style={{ width: 90 }}
          />
        </ActionableDialogActions>
      </form>
    </>
  );
});

export interface PartnerFormDialogProps extends PartnerContextValuesBase {}

export const PartnerFormDialog: React.FC<PartnerFormDialogProps> = (props) => {
  const { open, onClose, partnerId } = props;

  const dialogContentKey = useDialogKey(open);

  const partnerQuery = dashboardPartnerApis.useGet(
    '/dashboard/partner/:id',
    {
      params: {
        id: partnerId!,
      },
    },
    {
      enabled: !!partnerId,
    },
  );

  const contextValue = useShallowMemo(() => ({
    initialOutput: partnerQuery.data ? PartnerHelper.mapToPost(partnerQuery.data) : initialPartner,
    ...props,
  }));

  return (
    <PartnerContext.Provider value={contextValue}>
      <ActionableDialog open={open} onClose={onClose} size="lg" source={import.meta.url}>
        <QueryContainer queries={[partnerQuery]}>
          <PartnerDialogContent key={dialogContentKey} />
        </QueryContainer>
      </ActionableDialog>
    </PartnerContext.Provider>
  );
};
